import { brandCoreLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { SortOrder, updateSort } from "helpers/updateSort";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  Payment,
  handleSelectAll as onSelectAll,
  handlePaginate as onPaginate,
  handleSelect as onSelect,
  deletePaymentsItem,
  getPaymentsList,
  bunchUpdatePayments,
} from "store/cms/footer/payments/slice";
import { openModal } from "store/modals/slice";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { getIsActiveNumeric } from "helpers/getIsActiveNumeric";
import CmsPaymentsFilter from "./component/CmsPaymentsFilter";
import CmsPaymentsHeader from "./component/CmsPaymentsHeader";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

const PaymentsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState<number>(brandCoreLimits[0]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByGeo, setFilterByGeo] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [filterByActive, setFilterByActive] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<SortOrder<Payment>>({});
  const paymentsList = useAppSelector((state) => state["cms-payments"].list);
  const isPaymentsListFetched = useAppSelector(
    (state) => state["cms-payments"].isListFetched,
  );
  const totalItems = useAppSelector(
    (state) => state["cms-payments"].totalItems,
  );
  const totalPages = useAppSelector(
    (state) => state["cms-payments"].totalPages,
  );
  const activePage = useAppSelector(
    (state) => state["cms-payments"].activePage,
  );
  const selectedItems = useAppSelector(
    (state) => state["cms-payments"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (state) => state["cms-payments"].processingItemId,
  );

  const actionOptions = [
    { label: "Activate", value: "activate" },
    { label: "Hide", value: "hide" },
  ];

  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-payments',
    isListFetched: isPaymentsListFetched
  });

  useEffect(() => {
    if (!isPaymentsListFetched) {
      dispatch(
        getPaymentsList({
          limit,
          offset: activePage - 1,
          title: filterByTitle,
          countries: filterByGeo.map((g) => g.value as string),
          isActive: getIsActiveNumeric(filterByActive),
          order,
        }),
      );
    }
  }, [isPaymentsListFetched, activePage, limit]);

  const handleOrderClick = (key: keyof typeof order) => {
    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(onPaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(onSelectAll());
  };

  const handleSelect = (id: number) => {
    dispatch(onSelect(id));
  };

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(onPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(onPaginate(activePage + 1));
    }
  };

  const handleApplyFilter = () => {
    dispatch(onPaginate(1));
  };

  const handleDelete = (id: number) => {
    dispatch(deletePaymentsItem(id));
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.payments}`);

    dispatch(openModal({ activeModal: ModalTypes.payments }));
  };

  const handleEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.payments}&entityId=${id}`,
    );

    dispatch(openModal({ activeModal: ModalTypes.payments }));
  };

  const handleSelectAction = (value: string) => {
    if (!selectedItems.length) return;

    dispatch(
      bunchUpdatePayments({
        ids: selectedItems,
        isActive: value === "activate" ? 1 : 0,
      }),
    );
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CmsPaymentsFilter
        filterByTitle={filterByTitle}
        onFilterSearchByTitle={setFilterByTitle}
        filterByActive={filterByActive}
        onFilterByActive={setFilterByActive}
        filterByGeo={filterByGeo}
        onFilterByGeo={setFilterByGeo}
        handleApply={handleApplyFilter}
      />
      <div className="page-list--with-filter-content">
        <CmsPaymentsHeader
          options={actionOptions}
          selectAction={handleSelectAction}
          createModal={handleOpenCreateModal}
          isDisabled={selectedItems.length === 0 || !permissions?.update}
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
          
        />

        {!isPaymentsListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={6} />
            <PaginationSkeleton />
          </>
        )}

        {isPaymentsListFetched && paymentsList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noPaymentFound")} />
          </div>
        )}

        {isPaymentsListFetched && paymentsList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={paymentsList}
              idField="id"
              order={order}
              onSort={(v) => {
                const k = v as unknown;
                handleOrderClick(k as keyof typeof order);
              }}
              columns={{
                sortingColumns: {
                  geo: {
                    value: "geo",
                    hasSorting: true,
                  },
                },
              }}
              onAllSelect={handleSelectAll}
              onSelect={handleSelect}
              selectedItems={selectedItems}
              processingId={processingItemId}
              onDelete={handleDelete}
              hasDelete={permissions?.delete}
              hasEdit={permissions?.update}
              hasView={permissions?.read && !permissions?.update}
              hasOptions={permissions?.read || permissions?.update}
              {...actionProps}
            />
          </div>
        )}

        {isPaymentsListFetched && paymentsList.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(onPaginate(1));
            }}
            limitsArray={brandCoreLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePreviousClick}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentsList;
